import { Outlet } from 'react-router';
import { Button } from '@/components/Button';
import { ConnectionBanner } from '@/components/ConnectionBanner';
import { Icon } from '@/components/Icon';
import { useSignOutStaff, useStaffSession } from '@/auth/react';
import { env } from '@/lib/env';
import { MANAGER_BRANCH_SCREENS, SideNav, SkipLink, StripNav } from './nav';
import type { NavItem } from './nav';

/** Only areas the backend supports are listed: no empty entries for staff, settings or printers. */
export const MANAGE_NAV: NavItem[] = [
  { to: '/manage', label: 'Overview', icon: 'floor', end: true },
  { to: '/manage/locations', label: 'Locations', icon: 'pin' },
  { to: '/manage/menu', label: 'Menu', icon: 'menu' },
];

function RailLabel({ children }: { children: string }) {
  return (
    <p className="mb-1 px-3 text-xs font-semibold tracking-wide text-muted uppercase">{children}</p>
  );
}

/** The light management header: the product mark, "Management", the environment, who is signed in, sign out. */
export function ManageHeader() {
  const session = useStaffSession();
  const signOut = useSignOutStaff();
  return (
    <header className="flex min-h-14 items-center justify-between gap-3 border-b border-line bg-white px-4 md:px-6">
      <span className="flex min-w-0 items-baseline gap-2">
        <span className="text-xs font-semibold tracking-wide text-primary uppercase">
          ServeFlow
        </span>
        <p className="truncate text-base font-bold text-ink">Management</p>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {env.appEnv !== 'production' && (
          <span className="hidden rounded-full bg-neutral-soft px-2 py-0.5 text-xs font-semibold text-neutral-ink sm:inline">
            {env.appEnv}
          </span>
        )}
        <span className="hidden max-w-40 truncate text-sm text-muted sm:inline">
          {session?.user.displayName}
        </span>
        <Button variant="tertiary" onClick={signOut}>
          <Icon name="signOut" size={18} />
          Sign out
        </Button>
      </span>
    </header>
  );
}

/**
 * Management shell: a light header, a left rail from tablet width (the areas, then the other branch
 * screens a manager uses), a tab strip under the header on phones, and the canvas as the workspace.
 */
export function ManageShell() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <SkipLink />
      <ManageHeader />
      <ConnectionBanner />
      <StripNav items={MANAGE_NAV} label="Management mobile" />
      <div className="flex flex-1">
        <aside className="hidden w-60 shrink-0 border-r border-line bg-white p-3 md:block">
          <RailLabel>Areas</RailLabel>
          <SideNav items={MANAGE_NAV} label="Management" />
          <div className="mt-6">
            <RailLabel>Branch screens</RailLabel>
            <SideNav items={MANAGER_BRANCH_SCREENS} label="Branch screens" />
          </div>
        </aside>
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 px-4 py-4 md:px-6 md:py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
